import React, { useEffect, useState, useCallback } from "react";
import { Check, FileText, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { STATUSES, STATUS_COLOR, VERIF_LABEL, VERIF_COLOR } from "./common";
import IdReview from "./IdReview";

export default function Bookings() {
  const [list, setList] = useState([]);
  const [idBooking, setIdBooking] = useState(null);
  const [vfilter, setVfilter] = useState("all");
  const load = useCallback(() => api.get("/admin/bookings").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/bookings/${id}`, { status }); toast.success(`Marked ${status.replace("_", " ")}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const markPaid = async (id) => {
    try { await api.post(`/admin/bookings/${id}/mark-paid`, { method: "bank_transfer" }); toast.success("Payment recorded — booking confirmed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirmOverride = async (id) => {
    const reason = window.prompt("Reason for overriding ID verification & confirming this booking (required):");
    if (!reason || !reason.trim()) return;
    try { await api.post(`/admin/bookings/${id}/override`, { reason: reason.trim() }); toast.success("Verification overridden — booking confirmed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/bookings/${id}`); load(); };

  const counts = {
    all: list.length,
    awaiting: list.filter((b) => b.verification_status === "pending").length,
    pending_verification: list.filter((b) => b.status === "pending_verification").length,
    rejected: list.filter((b) => b.verification_status === "rejected").length,
  };
  const filtered = list.filter((b) =>
    vfilter === "all" ? true
      : vfilter === "awaiting" ? b.verification_status === "pending"
        : vfilter === "rejected" ? b.verification_status === "rejected"
          : vfilter === "pending_verification" ? b.status === "pending_verification" : true);

  return (
    <>
    {idBooking && <IdReview booking={idBooking} onClose={() => setIdBooking(null)} onChange={load} />}
    <div className="flex flex-wrap gap-2 mb-4" data-testid="booking-filters">
      {[["all", "All bookings"], ["awaiting", "Awaiting ID review"], ["pending_verification", "Pending verification"], ["rejected", "Rejected IDs"]].map(([k, label]) => (
        <button key={k} onClick={() => setVfilter(k)} data-testid={`bfilter-${k}`}
          className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${vfilter === k ? "bg-rose text-white" : "bg-sand text-forest hover:bg-rose/10"}`}>
          {label} <span className="opacity-70">({counts[k]})</span>
        </button>
      ))}
    </div>
    <div className="card-lux overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-bookings-table">
          <thead className="bg-sand/60 text-stone-500 text-xs uppercase tracking-wide">
            <tr><th className="text-left p-4">Guest</th><th className="text-left p-4">Suite</th><th className="text-left p-4">Dates</th><th className="text-left p-4">Total</th><th className="text-left p-4">Source</th><th className="text-left p-4">Status</th><th className="text-left p-4">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr key={b.id} className="border-t border-stone-100" data-testid={`booking-row-${b.id}`}>
                <td className="p-4"><div className="font-medium text-forest">{b.guest_name}</div><div className="text-xs text-stone-400">{b.guest_email}</div></td>
                <td className="p-4">{b.room_name}</td>
                <td className="p-4 text-xs">{b.check_in}<br />→ {b.check_out}</td>
                <td className="p-4 font-semibold">${b.total}
                  {b.balance_due > 0 && <div className="text-[11px] font-normal text-amber-600">bal ${b.balance_due}</div>}
                  {b.payment_status === "on_arrival" && <div className="text-[11px] font-normal text-blue-600">on arrival</div>}
                </td>
                <td className="p-4 capitalize text-xs">{b.source}</td>
                <td className="p-4">
                  <select value={b.status} onChange={(e) => setStatus(b.id, e.target.value)} data-testid={`booking-status-${b.id}`}
                    className={`rounded-full px-3 py-1 text-xs font-medium border-0 ${STATUS_COLOR[b.status]}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                  {b.verification_status && b.verification_status !== "none" && <div className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full ${VERIF_COLOR[b.verification_status] || ""}`} data-testid={`verif-badge-${b.id}`}>{VERIF_LABEL[b.verification_status]}</div>}
                </td>
                <td className="p-4">
                  <div className="flex gap-2">
                    {b.payment_status !== "paid" && (
                      <button onClick={() => markPaid(b.id)} data-testid={`mark-paid-${b.id}`} title="Mark paid (offline)" className="p-1.5 rounded-md bg-green-50 text-green-700 hover:bg-green-100"><Check size={15} /></button>
                    )}
                    <button onClick={() => setIdBooking(b)} data-testid={`id-review-${b.id}`} title="ID verification" className="p-1.5 rounded-md bg-stone-100 text-forest hover:bg-stone-200"><FileText size={15} /></button>
                    {b.status !== "confirmed" && (
                      <button onClick={() => confirmOverride(b.id)} data-testid={`override-confirm-${b.id}`} title="Override verification & confirm" className="p-1.5 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100"><ShieldCheck size={15} /></button>
                    )}
                    <button onClick={() => del(b.id)} title="Delete" className="p-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-100"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-stone-400">No bookings match this filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
    </>
  );
}
